/* ======================================
   Maison d'Etto v2 — Scent Finder
   <scent-finder>: intro → world → materials → feeling → result.
   Every label, tile and product (url, image, price, variant) comes from
   the section's <script type="application/json" data-v2-config> blob.
   Nothing here is copy.
   ====================================== */
(function () {
  'use strict';

  var ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  /* a string of words divided by · renders with the ink tick, never a middot (site owner, 15 Sep) */
  function tick(value) { return esc(value).replace(/\s*·\s*/g, '<span class="v2-tick" aria-hidden="true"></span>'); }
  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) { return ESC[c]; });
  }
  /* merchant-authored HTML (headings with <em>, richtext) — trusted as-is */
  function raw(value) { return value == null ? '' : String(value); }

  class ScentFinderElement extends HTMLElement {
    connectedCallback() {
      this.config = this.readConfig();
      this.root = this.querySelector('[data-finder-root]') || this;
      this.state = { step: 0, world: null, pool: null, result: null };
      this.onClick = this.handleClick.bind(this);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    readConfig() {
      var node = this.querySelector('script[data-v2-config]');
      var cfg = {};
      if (node) {
        try { cfg = JSON.parse(node.textContent); } catch (err) { console.error('[v2-scent-finder] invalid config', err); }
      }
      cfg.copy = cfg.copy || {};
      cfg.copy.intro = cfg.copy.intro || {};
      cfg.copy.steps = cfg.copy.steps || [];
      cfg.copy.progress = cfg.copy.progress || [];
      cfg.copy.result = cfg.copy.result || {};
      cfg.copy.result.facts = cfg.copy.result.facts || {};
      cfg.worlds = cfg.worlds || [];
      cfg.materials = cfg.materials || [];
      cfg.scents = cfg.scents || [];
      return cfg;
    }

    /* ---------- delegated actions ---------- */
    handleClick(e) {
      var target = e.target.closest('[data-action]');
      if (!target || !this.contains(target)) return;
      switch (target.dataset.action) {
        case 'start':
          e.preventDefault();
          this.go(1);
          break;
        case 'pick-world':
          e.preventDefault();
          this.state.world = target.dataset.id || null;
          this.state.pool = null;
          this.go(2);
          break;
        case 'pick-material': {
          e.preventDefault();
          var material = this.config.materials[parseInt(target.dataset.index, 10)];
          this.state.pool = material && material.scents ? material.scents.slice() : [];
          this.go(3);
          break;
        }
        case 'pick-scent':
          e.preventDefault();
          this.state.result = target.dataset.handle || null;
          this.go(4);
          break;
        case 'back':
          e.preventDefault();
          this.go(parseInt(target.dataset.step, 10) || 1);
          break;
        case 'retake':
          e.preventDefault();
          this.state = { step: 1, world: null, pool: null, result: null };
          this.go(1);
          break;
      }
    }

    go(step) {
      this.state.step = step;
      this.render();
      this.scrollIntoPlace();
    }

    scrollIntoPlace() {
      var top = this.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    /* ---------- data helpers ---------- */
    scentByHandle(handle) {
      return this.config.scents.filter(function (f) { return f.handle === handle; })[0] || null;
    }

    materialsFor(world) {
      return this.config.materials
        .map(function (m, i) { return { index: i, material: m }; })
        .filter(function (x) { return x.material.world === world; });
    }

    /* pool first, then the rest of the world, padded to at least three */
    feelingPool() {
      var scents = this.config.scents;
      var world = this.state.world;
      var pool = (this.state.pool || []).slice();
      scents.forEach(function (f) {
        if (f.world === world && pool.indexOf(f.handle) < 0) pool.push(f.handle);
      });
      if (pool.length < 3) {
        scents.forEach(function (f) { if (pool.indexOf(f.handle) < 0) pool.push(f.handle); });
        pool = pool.slice(0, 4);
      }
      var self = this;
      return pool.map(function (h) { return self.scentByHandle(h); }).filter(Boolean);
    }

    /* ---------- rendering ---------- */
    render() {
      var step = this.state.step;
      var out;
      if (step === 0) out = this.renderIntro();
      else if (step === 1) out = this.renderWorlds();
      else if (step === 2) out = this.renderMaterials();
      else if (step === 3) out = this.renderFeelings();
      else out = this.renderResult();
      this.root.innerHTML = out;
    }

    progress(current) {
      var labels = this.config.copy.progress;
      if (!labels.length) return '';
      return '<div class="v2-finder__progress">' + labels.map(function (label, i) {
        return '<span class="' + (i === current ? 'is-current' : '') + '">' + esc(label) + '</span>';
      }).join('') + '</div>';
    }

    stepHead(index) {
      var step = this.config.copy.steps[index] || {};
      var out = this.progress(index);
      if (step.title) out += '<h1 class="v2-display v2-finder__title">' + raw(step.title) + '</h1>';
      if (step.lead) out += '<p class="v2-editorial-line v2-finder__lead">' + raw(step.lead) + '</p>';
      return out;
    }

    backLink(step) {
      var label = this.config.copy.back;
      if (!label) return '';
      return '<button type="button" class="v2-text-link v2-finder__back" data-action="back" data-step="' + step + '">' + esc(label) + '</button>';
    }

    tile(attrs, image, name, line, lineClass) {
      var out = '<button type="button" class="v2-finder__tile" ' + attrs + '>';
      out += '<span class="v2-finder__plate">' + (image ? '<img src="' + esc(image) + '" alt="" loading="lazy">' : '') + '</span>';
      out += '<span class="v2-finder__tile-body">';
      if (name) out += '<span class="v2-finder__tile-name">' + esc(name) + '</span>';
      if (line) out += '<span class="v2-finder__tile-line' + (lineClass ? ' ' + lineClass : '') + '">' + tick(line) + '</span>';
      out += '</span></button>';
      return out;
    }

    renderIntro() {
      var intro = this.config.copy.intro;
      var out = '<div class="v2-finder__intro">';
      if (intro.label) out += '<span class="v2-caps-label v2-caps-label--moss">' + esc(intro.label) + '</span>';
      if (intro.heading) out += '<h1 class="v2-display v2-finder__title">' + raw(intro.heading) + '</h1>';
      if (intro.lead) out += '<div class="v2-editorial-line v2-finder__lead">' + raw(intro.lead) + '</div>';
      if (intro.button) out += '<button type="button" class="v2-btn v2-btn--solid" data-action="start">' + esc(intro.button) + '</button>';
      return out + '</div>';
    }

    renderWorlds() {
      var self = this;
      var out = this.stepHead(0);
      out += '<div class="v2-finder__options">' + this.config.worlds.map(function (w) {
        return self.tile('data-action="pick-world" data-id="' + esc(w.id) + '"', w.image, w.label, w.line);
      }).join('') + '</div>';
      return out;
    }

    renderMaterials() {
      var self = this;
      var options = this.materialsFor(this.state.world);
      if (!options.length) {
        /* nothing to ask — move straight to the feeling step for this world */
        this.state.pool = [];
        this.state.step = 3;
        return this.renderFeelings();
      }
      var out = this.stepHead(1);
      out += '<div class="v2-finder__options">' + options.map(function (x) {
        /* the material tile shows its own photograph when the block carries one; the lead product is the fallback */
        return self.tile('data-action="pick-material" data-index="' + x.index + '"', x.material.tile || x.material.image, '', x.material.label, 'v2-finder__tile-line--lead');
      }).join('') + '</div>';
      out += this.backLink(1);
      return out;
    }

    renderFeelings() {
      var self = this;
      var list = this.feelingPool();
      var out = this.stepHead(2);
      out += '<div class="v2-finder__options">' + list.map(function (f) {
        /* the feeling tile shows its own mood photograph when the block carries one; the result keeps the product */
        return self.tile('data-action="pick-scent" data-handle="' + esc(f.handle) + '"', f.tile || f.image, f.mood, f.shorthand);
      }).join('') + '</div>';
      out += this.backLink(this.materialsFor(this.state.world).length ? 2 : 1);
      return out;
    }

    renderResult() {
      var copy = this.config.copy.result;
      var f = this.scentByHandle(this.state.result);
      var out = '';
      if (copy.label) out += '<span class="v2-caps-label v2-caps-label--moss">' + esc(copy.label) + '</span>';
      if (!f) {
        if (copy.retake) out += '<p class="v2-finder__retake"><button type="button" class="v2-text-link" data-action="retake">' + esc(copy.retake) + '</button></p>';
        return out;
      }
      out += '<div class="v2-finder__result">';
      out += '<div class="v2-finder__media">' + (f.image ? '<img src="' + esc(f.image) + '" alt="' + esc(f.name) + '">' : '') + '</div>';
      out += '<div class="v2-finder__body" data-product-title="' + esc(f.name) + '">';
      if (f.name) out += '<h1 class="v2-display v2-finder__name">' + esc(f.name) + '</h1>';
      if (f.tagline) out += '<p class="v2-editorial-line v2-finder__tagline">' + esc(f.tagline) + '</p>';
      if (f.description) out += '<p class="v2-editorial-line v2-finder__desc">' + esc(f.description) + '</p>';

      var facts = [];
      if (f.shorthand && copy.facts.smells) facts.push([copy.facts.smells, f.shorthand]);
      if (f.horse && copy.facts.horse) facts.push([copy.facts.horse, f.horse + (f.temperament ? ' — ' + f.temperament.toLowerCase() : '')]);
      if (f.perfumer && copy.facts.perfumer) facts.push([copy.facts.perfumer, f.perfumer]);
      if (facts.length) {
        out += '<dl class="v2-facts v2-finder__facts">' + facts.map(function (row) {
          return '<div class="v2-fact"><dt>' + esc(row[0]) + '</dt><dd>' + tick(row[1]) + '</dd></div>';
        }).join('') + '</dl>';
      }

      var ctas = '';
      if (f.variantId && f.available) {
        if (copy.add) ctas += '<button type="button" class="v2-btn v2-btn--solid" data-cart-add data-variant-id="' + esc(f.variantId) + '" data-quantity="1" data-product-title="' + esc(f.name) + '">' + esc(copy.add) + (f.price ? ' &mdash; ' + esc(f.price) : '') + '</button>';
      } else if (copy.soldOut) {
        ctas += '<button type="button" class="v2-btn v2-btn--solid" disabled>' + esc(copy.soldOut) + '</button>';
      }
      if (f.url && copy.story) ctas += '<a class="v2-btn" href="' + esc(f.url) + '">' + esc(copy.story) + '</a>';
      if (ctas) out += '<div class="v2-finder__ctas">' + ctas + '</div>';

      if (copy.ladderHeading || copy.ladderText) {
        /* the $50 credit carries its micro line under every mention (CW IX.E) */
        out += '<div class="v2-ladder">' + (copy.ladderHeading ? '<strong>' + esc(copy.ladderHeading) + '</strong>' : '') + raw(copy.ladderText) + (copy.ladderMicro ? '<p class="v2-ladder__micro">' + esc(copy.ladderMicro) + '</p>' : '') + '</div>';
      }
      if (copy.retake) out += '<p class="v2-finder__retake"><button type="button" class="v2-text-link" data-action="retake">' + esc(copy.retake) + '</button></p>';
      out += '</div></div>';
      return out;
    }
  }

  if (!customElements.get('scent-finder')) {
    customElements.define('scent-finder', ScentFinderElement);
  }
})();
